import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import EmptyDataOverlay from "../../../components/empty-data-overlay";
import {
  getTeamMemberWeeklyReportsState,
  TeamMemberWeeklyDateReports,
  TeamMemberWeeklyTaskReports,
} from "../../../redux/features/report-slice";
import { ArrowDownOutlined } from "../../../components/icons/arrow-down-outlined";
import WeeklyReportsTimeline from "./reports-timeline";
import { formatDuration, fractionOfDayToTimeString } from "../../../core/utils";
import Loading from "../../../components/loading";

export default function WeeklyReportTab() {
  const teamMemberReports = useSelector(getTeamMemberWeeklyReportsState).data;

  const [totalDuration, setTotalDuration] = useState<number>(0);
  const [toggleTaskGroup, setToggleTaskGroup] = useState<{
    [id: string]: boolean;
  }>({});

  function mapToChartData(
    reports: TeamMemberWeeklyDateReports[]
  ): { name: string; data: number[] }[] {
    // Initialize arrays for duration and quantity
    const durationData: number[] = [];
    const quantityData: number[] = [];

    // Iterate over the reports array and push values to the corresponding arrays
    reports.forEach((report) => {
      durationData.push(report.totalDuration);
      quantityData.push(report.totalQuantity);
    });

    // Return the mapped data in the required format
    return [
      { name: "Working hours", data: durationData },
      { name: "Quantity", data: quantityData },
    ];
  }

  useEffect(() => {
    if (teamMemberReports) {
      const processedTags = new Set();
      const dur = teamMemberReports.taskGroups.reduce((sum, item) => {
        const progressSum = item.reports.reduce((reportSum, report) => {
          if (!report.tag || !processedTags.has(report.tag)) {
            if (report.tag) {
              processedTags.add(report.tag);
            }
            return reportSum + report.duration;
          }
          return reportSum;
        }, 0);
        return sum + progressSum;
      }, 0);
      setTotalDuration(dur);
    }
  }, [teamMemberReports]);

  return (
    <div
      className="space-y-4 overflow-auto"
      style={{
        height: "calc(100% - 58px)",
      }}
    >
      {teamMemberReports ? (
        <>
          <div className="flex flex-col">
            <label className="text-text-title">Total duration</label>
            <div className="space-x-2 flex items-center">
              <label className="text-text-primary text-xl">
                {formatDuration(totalDuration)}
              </label>
              <label>•</label>
              <label className="text-text-primary text-sm">{`${(
                (totalDuration / 40) *
                100
              ).toFixed(1)}%`}</label>
            </div>
          </div>
          {teamMemberReports.taskGroups.length > 0 ? (
            teamMemberReports.taskGroups.map(
              (report: TeamMemberWeeklyTaskReports, index: number) => {
                const orgName = report.taskGroup.taskFunction.organization.name;
                const orgColor =
                  orgName === "Head Office"
                    ? "#7d003c"
                    : orgName === "HCM Office"
                    ? "#0e7ab0"
                    : "#a37402";
                return (
                  <div
                    key={index}
                    className={`border border-divider rounded-lg cursor-pointer hover:bg-action-hover ${
                      toggleTaskGroup[report.taskGroup.id]
                        ? "bg-action-hover"
                        : "bg-background-paper"
                    }`}
                  >
                    <div
                      className="flex items-center justify-between p-4"
                      onClick={() => {
                        setToggleTaskGroup((prevState) => ({
                          ...prevState,
                          [report.taskGroup.id]:
                            !prevState[report.taskGroup.id],
                        }));
                      }}
                    >
                      <div className="w-8/12 flex items-center">
                        <ArrowDownOutlined
                          className={`h-4 mr-4 transition-transform duration-300 ${
                            toggleTaskGroup[report.taskGroup.id]
                              ? "transform rotate-180"
                              : ""
                          }`}
                        />
                        <div className="w-11/12 font-medium">
                          <label className="hover:underline cursor-pointer">
                            {report.taskGroup.title}
                          </label>
                          <div className="flex items-center space-x-1">
                            <label
                              className="text-xs whitespace-nowrap font-medium"
                              style={{ color: orgColor }}
                            >
                              {report.taskGroup.taskFunction.organization.name}
                            </label>
                            <label>•</label>
                            <label className="text-xs line-clamp-1 text-text-title">
                              {report.taskGroup.taskFunction.title}
                            </label>
                            {report.taskGroup.branch !== null && (
                              <label className="text-xs whitespace-nowrap text-text-title">
                                {`/ ${report.taskGroup.branch.name}`}
                              </label>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="w-2/12 flex flex-col">
                        <label className="text-sm text-text-title">
                          Total Duration
                        </label>
                        <label className="text-text-primary font-medium">
                          {report.totalDuration}
                        </label>
                      </div>
                      <div className="w-2/12 flex flex-col">
                        <label className="text-sm text-text-title">
                          Total Quantity
                        </label>
                        <label className="text-text-primary font-medium">
                          {report.totalQuantity}
                        </label>
                      </div>
                      <div className="w-2/12 flex flex-col">
                        <label className="text-sm text-text-title">
                          Avg. Time
                        </label>
                        <label className="text-text-primary font-medium">
                          {fractionOfDayToTimeString(
                            report.totalDuration /
                              (report.totalQuantity !== 0
                                ? report.totalQuantity
                                : 1)
                          )}
                        </label>
                      </div>
                    </div>
                    <div
                      className={`${
                        toggleTaskGroup[report.taskGroup.id]
                          ? "slide-down"
                          : "slide-up overflow-hidden"
                      }`}
                    >
                      <WeeklyReportsTimeline reports={report.reports} />
                    </div>
                  </div>
                );
              }
            )
          ) : (
            <EmptyDataOverlay label="No Reports found" exClassName="h-96" />
          )}
        </>
      ) : (
        <Loading exClassName="w-full h-full text-center content-center" />
      )}
    </div>
  );
}
